/**
 * City wastewater inventory: stored ClimatePlans wastewater row, scaled to
 * other years by service population (residents + workplace workers) or by
 * population when workers are unavailable.
 */

import { supabaseClient } from './supabase';
import {
  resolveWastewaterForInventoryYear,
  wastewaterGeofipsKeys,
  type StoredWastewaterObservation,
} from '../domains/ghgInventoryReport/wastewaterSource';

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export interface WastewaterActivitySnapshot {
  facilityName: string | null;
  treatmentVolumeAf: number | null;
  treatmentLevel: string | null;
  disposalMethod: string | null;
  inventoryYear: number | null;
  volumeMethod: string | null;
}

export interface ScaledWastewaterSeries {
  stored: StoredWastewaterObservation | null;
  activity: WastewaterActivitySnapshot | null;
  byYear: Record<number, number>;
  scaleBasisByYear: Record<number, string>;
  populationByYear: Record<number, number | null>;
}

export async function fetchStoredWastewater(
  geofips: number,
  year: number
): Promise<StoredWastewaterObservation | null> {
  const { data, error } = await supabaseClient
    .from('v_wastewater_inventory_summary')
    .select(
      'geofips,inventory_year,jurisdiction_name,wastewater_emissions_mtco2e,methodology_version,ef_version,coverage_status'
    )
    .lte('inventory_year', year)
    .in('geofips', wastewaterGeofipsKeys(geofips))
    .order('inventory_year', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  const mt = numOrNull((data as { wastewater_emissions_mtco2e?: number }).wastewater_emissions_mtco2e);
  if (mt == null) return null;
  return {
    geofips: String((data as { geofips?: string }).geofips ?? geofips),
    inventoryYear: Number((data as { inventory_year?: number }).inventory_year ?? year),
    mtCo2e: mt,
    methodologyVersion: (data as { methodology_version?: string | null }).methodology_version ?? null,
    efVersion: (data as { ef_version?: string | null }).ef_version ?? null,
    coverageStatus: (data as { coverage_status?: string | null }).coverage_status ?? null,
  };
}

async function fetchWastewaterActivity(
  geofips: number,
  inventoryYear: number
): Promise<WastewaterActivitySnapshot | null> {
  const { data, error } = await supabaseClient
    .from('wastewater_activity')
    .select('facility_name,treatment_volume_af,treatment_level,disposal_method,inventory_year,volume_method')
    .eq('inventory_year', inventoryYear)
    .in('geofips', wastewaterGeofipsKeys(geofips))
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return {
    facilityName: (data as { facility_name?: string | null }).facility_name ?? null,
    treatmentVolumeAf: numOrNull((data as { treatment_volume_af?: number }).treatment_volume_af),
    treatmentLevel: (data as { treatment_level?: string | null }).treatment_level ?? null,
    disposalMethod: (data as { disposal_method?: string | null }).disposal_method ?? null,
    inventoryYear: numOrNull((data as { inventory_year?: number }).inventory_year),
    volumeMethod: (data as { volume_method?: string | null }).volume_method ?? null,
  };
}

export async function fetchScaledWastewaterSeries(
  geofips: number,
  years: number[]
): Promise<ScaledWastewaterSeries> {
  const uniqueYears = Array.from(new Set(years.filter((y) => Number.isFinite(y)))).sort((a, b) => a - b);
  if (!uniqueYears.length) {
    return { stored: null, activity: null, byYear: {}, scaleBasisByYear: {}, populationByYear: {} };
  }

  const stored = await fetchStoredWastewater(geofips, Math.max(...uniqueYears));
  if (!stored) return { stored: null, activity: null, byYear: {}, scaleBasisByYear: {}, populationByYear: {} };

  const denomYears = Array.from(new Set([...uniqueYears, stored.inventoryYear]));
  const placeCode = String(geofips).slice(-6);
  const [{ data: popRows }, { data: jobRows }, activity] = await Promise.all([
    supabaseClient.from('population').select('year, population').eq('geofips', geofips).in('year', denomYears),
    supabaseClient
      .from('lodes_employees_workplace')
      .select('year, total_worker')
      .eq('geofips', placeCode)
      .eq('geography_type', 'place')
      .in('year', denomYears),
    fetchWastewaterActivity(geofips, stored.inventoryYear),
  ]);

  const popByYear: Record<number, number | null> = {};
  (popRows || []).forEach((row: { year?: number; population?: number }) => {
    if (row.year == null) return;
    popByYear[row.year] = numOrNull(row.population);
  });
  const workersByYear: Record<number, number | null> = {};
  (jobRows || []).forEach((row: { year?: number; total_worker?: number }) => {
    if (row.year == null) return;
    workersByYear[row.year] = numOrNull(row.total_worker);
  });

  const basePopulation = popByYear[stored.inventoryYear] ?? null;
  const baseWorkers = workersByYear[stored.inventoryYear] ?? null;
  const byYear: Record<number, number> = {};
  const scaleBasisByYear: Record<number, string> = {};

  uniqueYears.forEach((year) => {
    const resolved = resolveWastewaterForInventoryYear(stored, year, {
      inventoryPopulation: popByYear[year] ?? null,
      inventoryWorkers: workersByYear[year] ?? null,
      basePopulation,
      baseWorkers,
    });
    if (!resolved) return;
    byYear[year] = resolved.mtCo2e;
    scaleBasisByYear[year] = resolved.scaleBasis;
  });

  return { stored, activity, byYear, scaleBasisByYear, populationByYear: popByYear };
}
